/**
 * Parent day view. Shows ONE day at a time and lets the parent page back
 * through the treatment to see exactly what was entered — doses, the daily
 * check-in, weight, and observations.
 *
 * The dose section is the fast-entry list (DoseList): a dose given at its
 * scheduled time is one tap. Doses stay loggable on a past day, because a
 * forgotten evening dose is a common and clinically meaningful thing to record
 * late; a back-dated save is marked in the UI and dated to the day it was DUE,
 * never to the day it is typed. Nothing here changes a prescription or a dose.
 *
 * The non-dose sections are read-only summaries; "Edit this day's entries"
 * hands off to the Journal for the same date, so the entry forms live in
 * exactly one place.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CalendarDate } from '@toxofollow/shared';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card, Badge } from '../../components/ui.js';
import { DayPager, useDayParam } from '../../components/DayPager.js';
import { DoseList } from './DoseList.js';

export function ParentToday() {
  const { t } = useI18n();
  const [day, setDay, bounds, today] = useDayParam();

  // Shares DoseList's cache entry, so the summary costs no extra request.
  const sched = useQuery({
    queryKey: ['parent-schedule', day],
    queryFn: () => api.get<{ slots: { administration: unknown }[] }>(`/medications/schedule?date=${day}`),
  });
  const slots = sched.data?.slots ?? [];
  const doneN = slots.filter((s) => s.administration).length;
  const left = slots.length - doneN;
  const summary = `${left > 0 ? t('entry.summaryToLog', { n: left }) : t('entry.summaryAllDone')} · ${t('entry.summaryRecorded', { n: doneN })}`;

  return (
    <div>
      <h1 className="mb-1 text-xl font-bold text-slate-800">
        {day === today ? t('common.today') : t('day.title')}
      </h1>

      <DayPager
        day={day}
        bounds={bounds}
        today={today}
        onChange={setDay}
        extra={
          <>
            <div className="mt-1 text-center text-sm text-[#56616D]">{summary}</div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <Link to={`/journal?date=${day}`} className="text-brand-dark underline">
                {t('day.editEntries')}
              </Link>
              <Link to="/history" className="text-brand-dark underline">
                {t('history.open')}
              </Link>
            </div>
          </>
        }
      />

      <DoseList day={day} />

      <DaySummary day={day} />
    </div>
  );

  /**
   * What else was entered on this day. These three endpoints return the FULL
   * history newest-first, so one cached query serves every day the parent pages
   * to — stepping the cursor costs no extra request.
   */
  function DaySummary({ day: on }: { day: CalendarDate }) {
    interface CheckIn { date: string; maxTemperatureC: string | null; wetDiapers: number | null; stools: number | null; symptoms: string[] }
    interface Growth { date: string; weightGrams: string | null; headCircumferenceCm: string | null; weightVerified: boolean }
    interface Obs { id: string; date: string; text: string }

    const checkIns = useQuery({ queryKey: ['checkins'], queryFn: () => api.get<{ checkIns: CheckIn[] }>('/checkins') });
    const growth = useQuery({ queryKey: ['growth'], queryFn: () => api.get<{ growth: Growth[] }>('/growth') });
    const obs = useQuery({ queryKey: ['observations'], queryFn: () => api.get<{ observations: Obs[] }>('/observations') });

    const ci = checkIns.data?.checkIns.find((c) => c.date === on) ?? null;
    const gs = (growth.data?.growth ?? []).filter((g) => g.date === on);
    const os = (obs.data?.observations ?? []).filter((o) => o.date === on);
    const loading = checkIns.isLoading || growth.isLoading || obs.isLoading;
    const nothing = !loading && !ci && gs.length === 0 && os.length === 0;

    return (
      <div className="mt-6 space-y-3">
        <Card title={t('day.summaryCheckin')}>
          {ci ? (
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <div><dt className="text-slate-500">{t('checkin.temperature')}</dt><dd>{ci.maxTemperatureC ?? '—'}</dd></div>
              <div><dt className="text-slate-500">{t('checkin.wetDiapers')}</dt><dd>{ci.wetDiapers ?? '—'}</dd></div>
              <div><dt className="text-slate-500">{t('checkin.stools')}</dt><dd>{ci.stools ?? '—'}</dd></div>
              {ci.symptoms.length > 0 && (
                <div className="col-span-3 flex flex-wrap gap-1 pt-1">
                  {ci.symptoms.map((s) => <Badge key={s} tone="warn">{t(`symptom.${s}`)}</Badge>)}
                </div>
              )}
            </dl>
          ) : (
            <p className="text-sm text-slate-400">{loading ? '…' : '—'}</p>
          )}
        </Card>

        <Card title={t('day.summaryGrowth')}>
          {gs.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {gs.map((g, i) => (
                <li key={`${g.date}-${i}`} className="flex items-center gap-2">
                  <span>{g.weightGrams ? `${g.weightGrams} g` : '—'}</span>
                  <span className="text-slate-400">·</span>
                  <span>{g.headCircumferenceCm ? `${g.headCircumferenceCm} cm` : '—'}</span>
                  {!g.weightVerified && <Badge tone="info">{t('weight.unverified')}</Badge>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">{loading ? '…' : '—'}</p>
          )}
        </Card>

        <Card title={t('day.summaryObservations')}>
          {os.length > 0 ? (
            <ul className="list-disc space-y-1 pl-4 text-sm">
              {os.map((o) => <li key={o.id} className="whitespace-pre-wrap">{o.text}</li>)}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">{loading ? '…' : '—'}</p>
          )}
        </Card>

        {nothing && <p className="px-1 text-sm text-slate-400">{t('day.noEntries')}</p>}
      </div>
    );
  }
}
